/**
 * FileUpload Component
 * Simple file upload with drag & drop - NO INTERNAL STATE MANAGEMENT
 * Parent component manages all state via onUpload callback
 */

import React, { useState, useRef } from 'react';
import { Upload, AlertCircle } from 'lucide-react';

export default function FileUpload({
  label = 'Upload File',
  accept = '.jpg,.jpeg,.png,.pdf,.doc,.docx',
  maxSizeMB = 2,
  onUpload, // Parent handles what to do with the file
  disabled = false,
  className = ''
}) {
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  const handleDragOver = (e) => {
    e.preventDefault();
    if (!disabled) setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    if (disabled) return;

    const file = e.dataTransfer.files[0];
    if (file) handleFileSelection(file);
  };

  const handleFileInputChange = (e) => {
    const file = e.target.files[0];
    if (file) handleFileSelection(file);
  };

  const handleFileSelection = (file) => {
    setError(null);

    // Validate file size
    const fileSizeMB = file.size / (1024 * 1024);
    if (fileSizeMB > maxSizeMB) {
      setError(`File is too large. Maximum size is ${maxSizeMB}MB. Your file is ${fileSizeMB.toFixed(2)}MB.`);
      return;
    }

    // Validate file type
    const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
    const acceptedTypes = accept.split(',').map(t => t.trim().toLowerCase());
    const isValidType = acceptedTypes.some(type => {
      if (type.includes('/*')) {
        const category = type.split('/')[0];
        return file.type.startsWith(category + '/');
      }
      return type === fileExtension || file.type === type;
    });

    if (!isValidType) {
      setError(`File type not allowed. Accepted types: ${accept}`);
      return;
    }

    // Call parent's onUpload callback with the raw file
    // Parent decides what to do (process, save, etc.)
    if (onUpload) {
      onUpload(file);
    }

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleClick = () => {
    if (!disabled && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  return (
    <div className={`space-y-2 ${className}`}>
      {label && (
        <label className="block text-sm font-medium text-gray-700">
          {label}
        </label>
      )}

      <div
        onClick={handleClick}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`
          relative border-2 border-dashed rounded-lg p-6 text-center cursor-pointer
          transition-colors duration-200
          ${isDragging
            ? 'border-blue-500 bg-blue-50'
            : disabled
            ? 'border-gray-200 bg-gray-50 cursor-not-allowed'
            : 'border-gray-300 hover:border-gray-400 bg-white'
          }
        `}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={accept}
          onChange={handleFileInputChange}
          disabled={disabled}
          className="hidden"
        />

        <Upload className={`
          mx-auto h-12 w-12 mb-3
          ${disabled ? 'text-gray-300' : 'text-gray-400'}
        `} />

        <p className="text-sm text-gray-600 mb-1">
          {isDragging
            ? 'Drop file here'
            : 'Click to upload or drag and drop'
          }
        </p>
        <p className="text-xs text-gray-500">
          Accepted types: {accept}
        </p>
        <p className="text-xs text-gray-500">
          Maximum size: {maxSizeMB}MB
        </p>
      </div>

      {/* Error message */}
      {error && (
        <div className="flex items-start space-x-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3">
          <AlertCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
          <p>{error}</p>
        </div>
      )}
    </div>
  );
}
